import { useMemo } from 'react'
import { isURL } from 'validator'

const walledGardenDomains = [
  'linkedin.com',
  'twitter.com',
  'x.com',
  'facebook.com',
  'dropbox.com',
  'drive.google.com',
  'gmail.com',
  'quora.com',
  'hotmail.com',
  'pinterest.com',
  'youtube.com',
  'twitch.tv',
  'linktr.ee',
  'instagram.com',
  // Add more domains as needed
]

/**
 * Regex exception patterns for walledGardenDomains
 */
const walledGardenExceptions = [
  {
    pattern: /^https?:\/\/(www\.)?instagram\.com\/([a-zA-Z0-9._]+)\/?$/,
    description: 'Instagram individual profiles',
  },
  {
    pattern: /^https?:\/\/(www\.)?linkedin\.com\/pulse\/[a-zA-Z0-9-]+/,
    description: 'LinkedIn Pulse articles',
  },
]

const validationMessages: Record<string, string> = {
  'linktr.ee':
    "We don't support LinkTree or video URLs, like YouTube, for brand voice - please use a website URL instead!",
  'youtube.com': "We don't support video URLs for brand voice - please use a website URL instead!",
}

/**
 * Checks if a URL is crawlable by Blaze. A URL is crawlable if it is a valid URL and is not a
 * walled garden domain (as defined by the `walledGardenDomains` blacklist)
 * @param providedUrl A URL that the user has provided
 * @param customValidationMessages custom validation messages
 * @returns An object with two properties: `isValid` and `errorMessage`. `isValid` is a boolean
 * that indicates whether the URL is valid or not. `errorMessage` is a string that contains an
 * error message to be displayed to the user if the URL is invalid.
 */
export const useIsUrlCrawlable = (
  providedUrl: string,
  customValidationMessages: Record<string, string> = validationMessages,
) => {
  return useMemo(() => {
    return isUrlCrawlable(providedUrl, customValidationMessages)
  }, [providedUrl, customValidationMessages])
}

/**
 * Checks if a URL is crawlable by Blaze. A URL is crawlable if it is a valid URL and is not a
 * walled garden domain (as defined by the `walledGardenDomains` blacklist)
 * @param providedUrl A URL that the user has provided
 * @param customValidationMessages custom validation messages
 * @returns An object with two properties: `isValid` and `errorMessage`. `isValid` is a boolean
 * that indicates whether the URL is valid or not. `errorMessage` is a string that contains an
 * error message to be displayed to the user if the URL is invalid.
 */
export const isUrlCrawlable = (
  providedUrl: string,
  customValidationMessages: Record<string, string> = validationMessages,
) => {
  const isProvidedUrlValid = isURL(providedUrl, { require_tld: true, require_protocol: true })
  const isProvidedUrlValidWithHttps = isURL(`https://${providedUrl}`, { require_tld: true, require_protocol: true })

  let isValid = isProvidedUrlValid || isProvidedUrlValidWithHttps || providedUrl.length === 0

  let errorMessage = !isValid ? 'Please enter a valid URL' : undefined
  if (!isValid || providedUrl.length === 0) {
    return { isValid, errorMessage }
  }

  let url = isProvidedUrlValid ? providedUrl : `https://${providedUrl}`

  try {
    const urlObj = new URL(url)
    /**
     * some websites may have subdomains like blog.linkedin.com, which would be allowable, but we
     * can't differentiate between those and the walled garden domains without extensive checks,
     * so we just block them all
     */
    const walledGarden = walledGardenDomains.find(
      (domain) =>
        urlObj.hostname.includes(domain) && (urlObj.hostname === domain || urlObj.hostname.endsWith(`.${domain}`)),
    )

    if (!walledGarden || walledGardenExceptions.some(({ pattern }) => pattern.test(url))) {
      return { isValid: true, errorMessage: undefined }
    }

    return {
      isValid: false,
      errorMessage: customValidationMessages[walledGarden] || (
        <>
          Looking to analyze your social content?
          <br />
          Use the "Paste Text" option to add your example posts
        </>
      ),
    }
  } catch (error) {
    return { isValid: false, errorMessage: 'Please enter a valid URL' }
  }
}
